import { useEffect, useState } from "react";
import { toast } from "../lib/notify";
import { MapPin, Plus, X, Save, Pencil } from "lucide-react";
import { http, formatApiError } from "../lib/api";
import IL_ILCE from "../lib/trIlIlce.json";

const cls = "px-2 py-1.5 text-xs border border-slate-300 rounded-md bg-white outline-none focus:border-slate-800";
const CITIES = Object.keys(IL_ILCE);

// Geçici personelin çalışabileceği il / ilçeler: il seçilince ilçeleri çıkar, çoklu ilçe seçimi; "İL - İLÇE" kutuları
export default function WorkAreas({ personId, initial }) {
  const [areas, setAreas] = useState(initial || []);
  const [city, setCity] = useState("");
  const [picked, setPicked] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [editMode, setEditMode] = useState(!(initial && initial.length));
  useEffect(() => { setAreas(initial || []); setDirty(false); setEditMode(!(initial && initial.length)); }, [initial, personId]);
  const districts = city ? IL_ILCE[city] || [] : [];
  const chips = areas.flatMap((a) => (a.districts?.length ? a.districts.map((d) => ({ city: a.city, district: d })) : [{ city: a.city, district: "" }]));

  const add = () => {
    if (!city) return toast.error("Önce il seçin");
    setAreas((prev) => {
      const rest = prev.filter((a) => a.city !== city);
      const cur = prev.find((a) => a.city === city);
      const merged = [...new Set([...(cur?.districts || []), ...picked])].sort((a, b) => a.localeCompare(b, "tr"));
      return [...rest, { city, districts: merged }].sort((a, b) => a.city.localeCompare(b.city, "tr"));
    });
    setPicked([]); setDirty(true);
  };
  const removeChip = (c) => {
    setAreas((prev) => prev.flatMap((a) => {
      if (a.city !== c.city) return [a];
      if (!c.district) return [];
      const ds = a.districts.filter((d) => d !== c.district);
      return ds.length ? [{ ...a, districts: ds }] : [];
    }));
    setDirty(true);
  };
  const save = async () => {
    try { const r = await http.put(`/personnel/${personId}/work-areas`, { work_areas: areas }); setAreas(r.data.work_areas); setDirty(false); setEditMode(false); setCity(""); setPicked([]); toast.success("Çalışabileceği lokasyonlar kaydedildi"); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-2" data-testid="work-areas">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-sm font-bold text-emerald-900"><MapPin className="w-4 h-4" /> Çalışabileceği Lokasyonlar</span>
        {!editMode && <button type="button" onClick={() => setEditMode(true)} data-testid="wa-edit" className="ml-auto inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-emerald-700 text-emerald-800 bg-white text-xs font-semibold hover:bg-emerald-50"><Pencil className="w-3.5 h-3.5" /> Düzenle</button>}
        {editMode && <><select value={city} onChange={(e) => { setCity(e.target.value); setPicked([]); }} data-testid="wa-city" className={cls}><option value="">İl seçin…</option>{CITIES.map((c) => <option key={c} value={c}>{c}</option>)}</select>
        {city && <span className="text-[11px] text-slate-600">{picked.length ? `${picked.length} ilçe seçili` : "ilçe seçin (boş bırakırsanız ilin tamamı)"}</span>}
        <button type="button" onClick={add} disabled={!city} data-testid="wa-add" className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-emerald-700 text-white text-xs font-bold hover:bg-emerald-800 disabled:opacity-40"><Plus className="w-3.5 h-3.5" /> Ekle</button>
        <span className="ml-auto flex items-center gap-2">
          {(initial?.length > 0) && <button type="button" onClick={() => { setAreas(initial || []); setDirty(false); setEditMode(false); setCity(""); setPicked([]); }} data-testid="wa-cancel" className="px-2.5 py-1.5 rounded-md border border-slate-300 bg-white text-xs font-semibold hover:bg-slate-50">Vazgeç</button>}
          {dirty && <button type="button" onClick={save} data-testid="wa-save" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-slate-900 text-white text-xs font-bold hover:bg-slate-800"><Save className="w-3.5 h-3.5" /> Kaydet</button>}
        </span></>}
      </div>
      {editMode && city && (
        <div className="flex flex-wrap gap-1.5" data-testid="wa-districts">
          <button type="button" onClick={() => setPicked(picked.length === districts.length ? [] : [...districts])} data-testid="wa-district-all" className="px-2 py-1 rounded-md text-[11px] border border-emerald-600 text-emerald-800 bg-white hover:bg-emerald-50">{picked.length === districts.length ? "Hiçbiri" : "Tümü"}</button>
          {districts.map((d) => { const on = picked.includes(d); return <button key={d} type="button" onClick={() => setPicked(on ? picked.filter((x) => x !== d) : [...picked, d])} data-testid={`wa-district-${d}`} className={`px-2 py-1 rounded-md text-[11px] border transition-colors ${on ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-700 border-slate-300 hover:border-emerald-500"}`}>{d}</button>; })}
        </div>
      )}
      <div className="flex flex-wrap gap-2" data-testid="wa-chips">
        {chips.length === 0 && <span className="text-xs text-slate-400" data-testid="wa-empty">Henüz lokasyon eklenmedi — il seçip ilçeleri işaretleyin ve Ekle'ye basın.</span>}
        {chips.map((c) => (
          <span key={`${c.city}-${c.district}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-emerald-400 bg-white text-xs font-semibold text-emerald-900 shadow-sm" data-testid={`wa-chip-${c.city}-${c.district || "TUMU"}`}>
            {c.city} - {c.district || "TÜM İLÇELER"}
            <button type="button" onClick={() => removeChip(c)} className={`text-slate-400 hover:text-red-600 ${editMode ? "" : "hidden"}`} data-testid={`wa-chip-remove-${c.city}-${c.district || "TUMU"}`}><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
    </div>
  );
}
